

import { useState, useRef, useCallback } from "react";
import { Camera, X, RefreshCw, Check } from "lucide-react";

interface CameraCaptureProps {
  onCapture: (file: File, preview: string) => void;
  label?: string;
  description?: string;
  className?: string;
}

export default function CameraCapture({
  onCapture,
  label = "Take Photo",
  description = "Use camera to capture photo",
  className = "",
}: CameraCaptureProps) {
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const startCamera = useCallback(async () => {
    try {
      setError(null);
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { 
          facingMode: 'environment', // Use back camera on mobile
          width: { ideal: 1920 },
          height: { ideal: 1080 }
        },
        audio: false,
      });
      
      setStream(mediaStream);
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
    } catch (err) {
      console.error("Camera error:", err);
      setError("Unable to access camera. Please check permissions.");
    }
  }, []);

  const stopCamera = useCallback(() => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    setCapturedImage(null);
    setIsCameraOpen(false);
  }, [stream]);

  const capturePhoto = useCallback(() => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');

    if (!context) return;

    // Set canvas dimensions to match video
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // Draw video frame to canvas
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Convert to blob
    canvas.toBlob(
      async (blob) => {
        if (!blob) {
          setError("Failed to capture photo");
          return;
        }

        setIsProcessing(true);

        try {
          // Compress and convert to WebP
          const compressedBlob = await compressToWebP(blob, 0.85);
          
          // Create file
          const timestamp = new Date().getTime();
          const file = new File([compressedBlob], `task-photo-${timestamp}.webp`, {
            type: 'image/webp',
            lastModified: Date.now(),
          });

          // Create preview URL
          const previewUrl = URL.createObjectURL(compressedBlob);
          
          setCapturedImage(previewUrl);
          
          // Stop camera
          stopCamera();
          
          // Call parent handler
          onCapture(file, previewUrl);
        } catch (err) {
          console.error("Processing error:", err);
          setError("Failed to process photo");
        } finally {
          setIsProcessing(false);
        }
      },
      'image/webp',
      0.85
    );
  }, [onCapture, stopCamera]);

  const compressToWebP = (blob: Blob, quality: number): Promise<Blob> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        reject(new Error('Canvas context not available'));
        return;
      }

      img.onload = () => {
        // Calculate new dimensions (max 1920px)
        let width = img.width;
        let height = img.height;
        const maxDimension = 1920;

        if (width > maxDimension || height > maxDimension) {
          if (width > height) {
            height = (height / width) * maxDimension;
            width = maxDimension;
          } else {
            width = (width / height) * maxDimension;
            height = maxDimension;
          }
        }

        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (compressedBlob) => {
            if (!compressedBlob) {
              reject(new Error('Compression failed'));
              return;
            }
            resolve(compressedBlob);
          },
          'image/webp',
          quality
        );
      };

      img.onerror = () => reject(new Error('Failed to load image'));
      
      const reader = new FileReader();
      reader.onload = (e) => {
        img.src = e.target?.result as string;
      };
      reader.onerror = () => reject(new Error('Failed to read blob'));
      reader.readAsDataURL(blob);
    });
  };

  const handleOpenCamera = async () => {
    setIsCameraOpen(true);
    await startCamera();
  };

  const handleRetake = () => {
    if (capturedImage) {
      URL.revokeObjectURL(capturedImage);
    }
    setCapturedImage(null);
    startCamera();
  };

  return (
    <div className={className}>
      <label className="block text-[10px] font-medium text-stone-500 mb-1">
        {label}
      </label>

      {/* Camera Button / Preview */}
      {!isCameraOpen && !capturedImage ? (
        <button
          type="button"
          onClick={handleOpenCamera}
          className="w-full p-4 border-2 border-dashed border-stone-300 rounded-lg bg-stone-50 hover:border-amber-400 hover:bg-amber-50 transition-all flex flex-col items-center gap-2"
        >
          <Camera className="w-8 h-8 text-stone-400" />
          <span className="text-sm font-medium text-stone-700">{label}</span>
          <span className="text-xs text-stone-500">{description}</span>
        </button>
      ) : capturedImage ? (
        <div className="relative">
          <img
            src={capturedImage}
            alt="Captured"
            className="w-full h-48 object-cover rounded-lg border border-stone-200"
          />
          <div className="absolute top-2 right-2 flex gap-2">
            <button
              type="button"
              onClick={handleRetake}
              className="p-2 bg-white/90 hover:bg-white rounded-lg shadow-md transition-colors"
              title="Retake"
            >
              <RefreshCw className="w-4 h-4 text-stone-600" />
            </button>
            <button
              type="button"
              onClick={() => {
                URL.revokeObjectURL(capturedImage);
                setCapturedImage(null);
              }}
              className="p-2 bg-white/90 hover:bg-white rounded-lg shadow-md transition-colors"
              title="Remove"
            >
              <X className="w-4 h-4 text-red-600" />
            </button>
          </div>
          <div className="absolute bottom-2 left-2 px-2 py-1 bg-emerald-500 text-white text-xs rounded-md flex items-center gap-1">
            <Check className="w-3 h-3" />
            Captured
          </div>
        </div>
      ) : (
        <div className="relative">
          <video
            ref={videoRef}
            autoPlay
            playsInline
            className="w-full h-64 object-cover rounded-lg border border-stone-200 bg-black"
          />
          <canvas ref={canvasRef} className="hidden" />
          
          {/* Camera Controls */}
          <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-3">
            <button
              type="button"
              onClick={capturePhoto}
              disabled={isProcessing}
              className="px-6 py-3 bg-amber-500 hover:bg-amber-600 text-white rounded-full font-semibold shadow-lg disabled:opacity-50 transition-colors flex items-center gap-2"
            >
              {isProcessing ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <Camera className="w-5 h-5" />
                  Capture
                </>
              )}
            </button>
            <button
              type="button"
              onClick={stopCamera}
              className="px-4 py-3 bg-white/90 hover:bg-white text-stone-700 rounded-full font-semibold shadow-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {error && (
            <div className="absolute top-4 left-4 right-4 p-3 bg-rose-500 text-white text-sm rounded-lg">
              {error}
            </div>
          )}
        </div>
      )}

      {description && !isCameraOpen && !capturedImage && (
        <p className="text-[9px] text-stone-400 mt-1">{description}</p>
      )}
    </div>
  );
}
